/**
 * Created: 2026-09-XX
 * Initial commit: Add coach application shell
 *
 * Coach application root: router + MainWindow chrome + coach nav items.
 * Mirrors the admin app's structure (AdminApp.tsx).
 * Updated: 2026-10-21 — Calls message.init(), without which message.prompt()
 * (e.g. the appointment cancel confirmation) silently does nothing.
 * Updated: 2026-10-21 — 课程日历: the coach calendar page, first in 教学管理.
 * Updated: 2026-10-22 — The placeholder home page is gone; / opens 课程日历.
 * Updated: 2026-10-22 — The 课程管理 group is renamed 教学管理, and its 课程发布
 * item 课程管理.
 * Updated: 2026-10-24 — 课程统计: completed sessions with income and court cost.
 */

import { message } from '@homemaster/jslib'
import SchoolIcon from '@mui/icons-material/School'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { zhCN as muiZhCN } from '@mui/x-date-pickers/locales'
import { LicenseInfo } from '@mui/x-license'
import { zhCN } from 'date-fns/locale'
import { useEffect } from 'react'
import { createBrowserRouter, Navigate } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import MainWindow, { type NavItem } from '../MainWindow'
import CourseEntitlementsPage from '../pages/CourseEntitlementsPage'
import CoursesPage from '../pages/CoursesPage/CoursesPage'
import SessionStatsPage from '../pages/SessionStatsPage'
import SessionsPage from '../pages/SessionsPage'
import CoachCalendarPage from './CoachCalendarPage'

// register MUI-X license key, expires on Jan. 25, 2027
LicenseInfo.setLicenseKey(
  '64a59fe7cef759ee6dc51c9b876f1d97Tz0xMjM3MzgsRT0xODAwOTIxNTk5MDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMS0yMDI2LFE9MSxBVD1tdWx0aSxLVj0y',
)

const navItems: NavItem[] = [
  {
    group: '教学管理',
    icon: <SchoolIcon />,
    children: [
      { text: '课程日历', path: '/calendar' },
      { text: '课程管理', path: '/courses' },
      { text: '已售课时', path: '/course-entitlements' },
      { text: '课程预约', path: '/sessions' },
      { text: '课程统计', path: '/session-stats' },
    ],
  },
]

function CoachMainWindow() {
  return <MainWindow navItems={navItems} />
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <CoachMainWindow />,
    children: [
      { index: true, element: <Navigate to="/calendar" replace /> },
      { path: 'calendar', element: <CoachCalendarPage /> },
      { path: 'courses', element: <CoursesPage /> },
      { path: 'course-entitlements', element: <CourseEntitlementsPage /> },
      { path: 'sessions', element: <SessionsPage /> },
      { path: 'session-stats', element: <SessionStatsPage /> },
    ],
  },
])

export default function CoachApp() {
  // Mounts the host for message.* toasts and prompts, as AppMain does.
  useEffect(() => {
    message.init()
  }, [])

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={zhCN}
      localeText={muiZhCN.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <RouterProvider router={router} />
    </LocalizationProvider>
  )
}
